import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { PlansDisclaimer } from '@/components/PlansDisclaimer';

const footerLinks = {
  explore: [
    { label: 'Residences', href: '/residences' },
    { label: 'Amenities', href: '/amenities' },
    { label: 'Commercial', href: '/commercial' },
    { label: 'Invest', href: '/invest' },
    { label: 'About Us', href: '/about' },
  ],
  connect: [
    { label: 'Register Interest', href: '#register' },
    { label: 'Book Consultation', href: '#consult' },
    { label: 'Lease Commercial Space', href: 'mailto:info@thepearl.ky?subject=Commercial Space Inquiry' },
  ],
};

const socialLinks = [
  { icon: Facebook, label: 'Facebook', href: 'https://facebook.com/thepearlgrandharbour' },
  { icon: Instagram, label: 'Instagram', href: 'https://instagram.com/thepearlgrandharbour' },
  { icon: Linkedin, label: 'LinkedIn', href: 'https://linkedin.com/company/thepearlgrandharbour' },
];

export const Footer = () => {
  return (
    <footer className="bg-deep-navy text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="text-center md:text-left">
            <img src="/logo-wh.png" alt="The Pearl at Grand Harbour" className="h-[77px] lg:h-[96px] w-auto object-contain mb-6 mx-auto md:mx-0" />
            <p className="text-white/80 text-sm mb-6 font-medium">Grand Harbour, Grand Cayman, Cayman Islands</p>
            <p className="text-white/70 text-sm leading-relaxed">
              Lagoon-side luxury living for professionals, families, and investors.
            </p>
          </div>

          {/* Explore */}
          <div>
            <h3 className="font-display text-xl font-semibold mb-6">Explore</h3>
            <ul className="space-y-3">
              {footerLinks.explore.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200 hover:translate-x-1 inline-block"
                    onClick={() => trackEvent('cta_click', { location: 'footer', target: link.label })}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h3 className="font-display text-xl font-semibold mb-6">Connect</h3>
            <ul className="space-y-3">
              {footerLinks.connect.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200 hover:translate-x-1 inline-block"
                    onClick={() => trackEvent('cta_click', { location: 'footer', target: link.label })}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-display text-xl font-semibold mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-white/80">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-brand-teal" />
                <span>Grand Harbour, Grand Cayman, Cayman Islands, KY</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-white/80">
                <Phone className="w-5 h-5 mt-0.5 flex-shrink-0 text-brand-teal" />
                <a href="tel:+13451234567" className="hover:text-white transition-colors duration-200">
                  +1 (345) 123-4567
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm text-white/80">
                <Mail className="w-5 h-5 mt-0.5 flex-shrink-0 text-brand-teal" />
                <a href="mailto:info@thepearl.ky" className="hover:text-white transition-colors duration-200">
                  info@thepearl.ky
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Social Links */}
        <div className="flex items-center justify-center gap-8 py-12 border-t border-white/10">
          {socialLinks.map((social) => {
            const Icon = social.icon;
            return (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-teal hover:scale-110 transition-all duration-300 group"
                aria-label={social.label}
                onClick={() => trackEvent('cta_click', { location: 'footer', target: social.label })}
              >
                <Icon className="w-6 h-6 group-hover:text-white" />
              </a>
            );
          })}
        </div>

        {/* Legal */}
        <div className="text-center pt-12 border-t border-white/10">
          <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-4xl mx-auto">
            Artist's impressions shown. All features, specifications, and timelines are subject to approvals 
            and change without notice. This is not an offer or contract. E&OE.
          </p>
          
          {/* Architectural Plans Disclaimer */}
          <div className="mb-6 max-w-4xl mx-auto">
            <p className="text-white/60 text-xs sm:text-sm leading-relaxed">
              The architectural plans, renderings, graphic images, and any other illustrative materials ("Plans and Images") displayed on this website are intended for marketing and representational purposes only. They are not intended to be, and should not be construed as, the final, definitive plans for construction.
            </p>
          </div>

          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} The Pearl at Grand Harbour. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
